import {
    ChangeEvent,
    FunctionComponent,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    Fade,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { LoadingContainer } from '@/components/Layout/Container';
import {
    ConditionLink,
    ImportedConditionLink,
    isOfTypeOriginalConditionLink,
} from '@/models/conditionLink';
import { useAuthentication } from '@/hooks/useAuthentication';
import { LoadingButton } from '@mui/lab';
import { useGetProductPersonNameConfigurationQuery } from '@/api/v2/textReplacement';
import { getPersonById } from '@/models/textReplacement';
import { useNotification } from '@starjack/features/notifications';
import {
    usePutConditionLinkMutation,
    usePutImportedConditionLinkMutation,
} from '@/api/v2/conditionLinks';

import { useGetLiftOperatorsQuery } from '@/api/v2/liftOperator';
import { useGetProductConfigurationsQuery } from '@/api/v2/productConfigurations';
import { ProductConfiguration } from '@/models/product';
import Link from '@mui/material/Link';
import {
    ConditionLinkPersonTypeMapping,
    PersonTypeMap,
} from '@/pages/rulesets/components/ImportedCondtionLinkPersonTypeMapping';

type ConditionLinkGeneralDataCardProps = {
    conditionLink: ConditionLink | ImportedConditionLink;
};

export const ConditionLinkGeneralDataCard: FunctionComponent<
    ConditionLinkGeneralDataCardProps
> = ({ conditionLink }) => {
    const [internalConditionLink, setInternalConditionLink] = useState<
        ConditionLink | ImportedConditionLink
    >(conditionLink);
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { data: liftOperators } = useGetLiftOperatorsQuery();
    const [putImportedConditionLink, { isLoading: putImportedLoading }] =
        usePutImportedConditionLinkMutation();
    const [putConditionLink, { isLoading: putCondtionLinkLoading }] =
        usePutConditionLinkMutation();
    const { data: personsMap } =
        useGetProductPersonNameConfigurationQuery(liftOperatorId);
    const { data: productConfigurations = [] } =
        useGetProductConfigurationsQuery({
            liftOperatorId,
        });
    const productConfigurationInUsage: ProductConfiguration[] = useMemo(() => {
        if (
            !internalConditionLink?.usages.length ||
            !productConfigurations?.length
        ) {
            return [];
        }

        const usedProductConfigurations = [];

        internalConditionLink.usages.forEach((condition) => {
            const productConfig = productConfigurations.find(
                (product) => product.productId === condition.shortProductId
            );

            if (productConfig) {
                usedProductConfigurations.push(productConfig);
            }
        });

        return usedProductConfigurations;
    }, [internalConditionLink, productConfigurations]);

    const { addNotification } = useNotification();

    const onConditionLinkSave = () => {
        let response = null;
        if (isOfTypeOriginalConditionLink(internalConditionLink)) {
            response = putConditionLink({
                liftOperatorId,
                conditionLink: internalConditionLink,
            });
        } else {
            response = putImportedConditionLink({
                liftOperatorId,
                importedConditionLink: {
                    ...internalConditionLink,
                    missingPersonTypesMapping: [],
                },
            });
        }

        response
            .unwrap()
            .then(() => {
                addNotification({
                    type: 'success',
                    message: 'Regelwerk erfolgreich gespeichert',
                });
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Speichern. Bitte später noch einmal versuchen',
                });
            });
    };

    const onUpdatePersonMapping = (personTypeMapping: PersonTypeMap) => {
        if (!isOfTypeOriginalConditionLink(internalConditionLink)) {
            setInternalConditionLink((prev) => ({
                ...prev,
                personTypesMapping: personTypeMapping,
            }));
        }
    };

    const updateConditionLink =
        (key: keyof ConditionLink) => (e: ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;

            setInternalConditionLink((prev) => ({
                ...prev,
                [key]: value,
            }));
        };

    useEffect(() => {
        if (isOfTypeOriginalConditionLink(conditionLink)) {
            setInternalConditionLink(conditionLink);
        } else {
            setInternalConditionLink((imported: ImportedConditionLink) => {
                const copyImported = { ...imported };

                imported.missingPersonTypesMapping.forEach((personType) => {
                    copyImported.personTypesMapping = {
                        ...copyImported.personTypesMapping,
                        [personType.id]: null,
                    };
                });

                return copyImported;
            });
        }
    }, [conditionLink]);

    return (
        <Card
            sx={{
                position: 'relative',
            }}
        >
            <CardContent
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <Typography variant='h6'>Allgemeine Daten</Typography>

                <LoadingButton
                    sx={{
                        ml: 'auto',
                    }}
                    onClick={onConditionLinkSave}
                    variant='contained'
                    loading={putImportedLoading || putCondtionLinkLoading}
                    disabled={!internalConditionLink.name}
                >
                    Regelwerk speichern
                </LoadingButton>
            </CardContent>

            <Divider
                sx={{
                    width: '100%',
                }}
            />

            <CardContent>
                <Stack
                    sx={{
                        mb: 4,
                    }}
                    spacing={2}
                >
                    <TextField
                        sx={{
                            width: 400,
                        }}
                        value={internalConditionLink.id}
                        onChange={updateConditionLink('id')}
                        label='UUID'
                        disabled={true}
                    />

                    <TextField
                        sx={{
                            width: 400,
                        }}
                        value={internalConditionLink.name}
                        onChange={updateConditionLink('name')}
                        label='Name *'
                    />
                </Stack>

                {isOfTypeOriginalConditionLink(internalConditionLink) && (
                    <>
                        {!!internalConditionLink.personTypes?.length && (
                            <Box
                                sx={{
                                    mb: 4,
                                }}
                            >
                                <Typography
                                    variant={'subtitle1'}
                                    sx={{
                                        mb: 3,
                                    }}
                                >
                                    Verwendete Personentypen:
                                </Typography>

                                {!!personsMap && (
                                    <Stack
                                        sx={{
                                            ml: 2,
                                        }}
                                        spacing={2}
                                    >
                                        {internalConditionLink.personTypes.map(
                                            (personType) => (
                                                <Typography key={personType.id}>
                                                    {
                                                        getPersonById(
                                                            personType.id,
                                                            personsMap.persons
                                                        )?.name
                                                    }
                                                </Typography>
                                            )
                                        )}
                                    </Stack>
                                )}
                            </Box>
                        )}
                    </>
                )}

                {!isOfTypeOriginalConditionLink(internalConditionLink) &&
                    !!Object.keys(
                        internalConditionLink?.personTypesMapping || {}
                    ).length && (
                        <Box
                            sx={{
                                mb: 4,
                            }}
                        >
                            <ConditionLinkPersonTypeMapping
                                personsToMap={
                                    internalConditionLink.personTypesMapping
                                }
                                personTypes={
                                    internalConditionLink.original.personTypes
                                }
                                onMappingChange={onUpdatePersonMapping}
                                showMissingWithError={true}
                            />
                        </Box>
                    )}

                {!!productConfigurationInUsage.length && (
                    <Box
                        sx={{
                            mb: 4,
                        }}
                    >
                        <Typography
                            variant={'subtitle1'}
                            sx={{
                                mb: 3,
                            }}
                        >
                            Verwendet in Produktkonfigurationen:
                        </Typography>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                            }}
                        >
                            {productConfigurationInUsage.map((pC) => (
                                <Chip
                                    key={pC.id}
                                    label={
                                        <Link
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            sx={{
                                                textDecoration: 'none',
                                                color: 'text.primary',
                                            }}
                                            href={`/produktkonfiguration/details/?id=${pC.productId}`}
                                        >
                                            {pC.product.name.de}
                                        </Link>
                                    }
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                {isOfTypeOriginalConditionLink(internalConditionLink) &&
                    !!liftOperators?.length &&
                    !!liftOperators?.length &&
                    !!internalConditionLink.imports.length && (
                        <Box
                            sx={{
                                mb: 4,
                            }}
                        >
                            <Typography
                                variant={'subtitle1'}
                                sx={{
                                    mb: 3,
                                }}
                            >
                                Regelwerk importiert von:
                            </Typography>

                            <Stack
                                sx={{
                                    ml: 2,
                                }}
                                spacing={2}
                            >
                                {internalConditionLink.imports.map(
                                    (imported) => (
                                        <Typography
                                            sx={{
                                                cursor: 'pointer',
                                                textDecorationColor:
                                                    '#1975D1 !important',
                                            }}
                                            key={imported.id}
                                        >
                                            {
                                                liftOperators.find(
                                                    (l) =>
                                                        l.id ===
                                                        imported.liftOperatorId
                                                )?.name?.de
                                            }{' '}
                                            - {imported.name}
                                        </Typography>
                                    )
                                )}
                            </Stack>
                        </Box>
                    )}
            </CardContent>

            <Fade in={putImportedLoading || putCondtionLinkLoading}>
                <div>
                    <LoadingContainer cover />
                </div>
            </Fade>
        </Card>
    );
};
